import {
  ArrowLeft,
  Watch,
  Wifi,
  Moon,
  Activity,
  CheckCircle2,
} from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { Progress } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/progress";
import { Badge } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/badge";
import { useNavigate } from "react-router-dom";

const ImproveAccuracy = () => {
  const navigate = useNavigate();

  const steps = [
    {
      icon: <Watch className="h-5 w-5" />,
      title: "Connect Fitbit",
      description:
        "Link your Fitbit device to get real-time HRV, heart rate, and sleep data.",
      status: "connected",
      impact: "+20% accuracy",
    },
    {
      icon: <Moon className="h-5 w-5" />,
      title: "Wear During Sleep",
      description:
        "Wearing your tracker overnight enables breathing rate, SpO2, and sleep staging data.",
      status: "partial",
      impact: "+15% accuracy",
    },
    {
      icon: <Activity className="h-5 w-5" />,
      title: "Log Activities",
      description:
        "Recording workouts and active minutes helps calibrate your recovery baseline.",
      status: "needed",
      impact: "+10% accuracy",
    },
    {
      icon: <Wifi className="h-5 w-5" />,
      title: "Sync Regularly",
      description:
        "Sync at least once daily for the most up-to-date stress calculations.",
      status: "needed",
      impact: "+5% accuracy",
    },
  ];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "connected":
        return (
          <Badge className="rounded-full border-0 bg-secondary/15 text-[10px] text-secondary">
            ✓ Done
          </Badge>
        );
      case "partial":
        return (
          <Badge className="bg-warning/15 text-warning rounded-full border-0 text-[10px]">
            Partial
          </Badge>
        );
      default:
        return (
          <Badge className="rounded-full border-0 bg-muted text-[10px] text-muted-foreground">
            Needed
          </Badge>
        );
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="mx-auto max-w-md space-y-4 p-4">
        <div className="flex items-center gap-3 pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/fitness2")}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground">
            Improve Accuracy
          </h1>
        </div>

        {/* Current Confidence */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-1 font-semibold text-foreground">
            Current Confidence
          </h3>
          <div className="mb-3 flex items-baseline gap-2">
            <span className="text-4xl font-black text-foreground">70%</span>
            <span className="text-sm text-muted-foreground">modeled</span>
          </div>
          <Progress value={70} className="mb-2 h-3" />
          <p className="text-xs text-muted-foreground">
            Complete the steps below to reach 95%+ confidence
          </p>
        </Card>

        {/* Steps */}
        <div className="space-y-3">
          <h2 className="px-1 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Steps to Improve
          </h2>
          {steps.map((step, i) => (
            <Card key={i} className="rounded-xl border-0 p-4 shadow-sm">
              <div className="flex items-start gap-3">
                <div
                  className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${step.status === "connected" ? "bg-secondary/10 text-secondary" : "bg-muted text-muted-foreground"}`}
                >
                  {step.icon}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex items-center gap-2">
                    <h4 className="text-sm font-semibold text-foreground">
                      {step.title}
                    </h4>
                    {getStatusBadge(step.status)}
                  </div>
                  <p className="mb-2 text-xs text-muted-foreground">
                    {step.description}
                  </p>
                  <Badge variant="outline" className="rounded-full text-[10px]">
                    {step.impact}
                  </Badge>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Potential Confidence */}
        <Card className="rounded-xl border-0 bg-primary/5 p-4 shadow-sm">
          <h3 className="mb-2 font-semibold text-foreground">
            With All Steps Complete
          </h3>
          <div className="mb-2 flex items-baseline gap-2">
            <span className="text-3xl font-black text-primary">95%+</span>
            <span className="text-sm text-muted-foreground">confidence</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Full signal coverage enables precise, real-time stress index
            calculations with minimal modeling assumptions.
          </p>
        </Card>
      </div>
    </div>
  );
};

export default ImproveAccuracy;
